// ShareLinksManager.js - Verwalte alle Share Links mit QR-Codes
import BodyScrollLock from './components/BodyScrollLock';
import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';

function ShareLinksManager({ token, onClose }) {
  const [shareLinks, setShareLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrCodeModal, setQrCodeModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);
  const showToast = (msg, type) => { setToastMsg({ msg, type }); setTimeout(() => setToastMsg(null), 4000); };

  useEffect(() => {
    loadShareLinks();
  }, []);

  const loadShareLinks = async () => {
    try {
      const response = await fetch('/api/share/my-links', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load share links');

      const data = await response.json();
      setShareLinks(data.share_links || []);
    } catch (error) {
      console.error('Load share links error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (shareId) => {
    try {
      const response = await fetch(`/api/share/${shareId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete share link');

      setShareLinks(shareLinks.filter(link => link.id !== shareId));
      setDeleteModal(null);
      showToast('Share-Link gelöscht!', 'success');
    } catch (error) {
      console.error('Delete error:', error);
      showToast('Fehler beim Löschen', 'error');
    }
  };

  const generateQRCode = async (url) => {
    try {
      const qrDataUrl = await QRCode.toDataURL(url, {
        width: 300,
        margin: 2,
        color: {
          dark: '#0891b2',
          light: '#ffffff'
        }
      });
      setQrCodeModal({ url, qrDataUrl });
    } catch (error) {
      console.error('QR Code generation error:', error);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Link kopiert!', 'success');
    } catch (error) {
      console.error('Copy error:', error);
    }
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.href = qrCodeModal.qrDataUrl;
    link.download = 'qr-code.png';
    link.click();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (link) => {
    if (link.is_expired) return 'red';
    if (link.is_limit_reached) return 'orange';
    return 'green';
  };

  const getStatusText = (link) => {
    if (link.is_expired) return 'Abgelaufen';
    if (link.is_limit_reached) return 'Limit erreicht';
    return 'Aktiv';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <BodyScrollLock />
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Lade Share Links...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <BodyScrollLock />
      <div className="relative max-w-6xl w-full my-4">
      {toastMsg && (
        <div onClick={() => setToastMsg(null)} style={{
          position: 'fixed', top: '1rem', left: '50%', transform: 'translateX(-50%)',
          zIndex: 10000, maxWidth: '400px', width: '90%', padding: '0.75rem 1rem',
          background: toastMsg.type === 'success' ? 'rgba(74,222,128,0.15)' : 'rgba(255,80,80,0.15)',
          border: '1px solid ' + (toastMsg.type === 'success' ? 'rgba(74,222,128,0.3)' : 'rgba(255,80,80,0.3)'),
          borderRadius: '10px', color: toastMsg.type === 'success' ? '#4ade80' : '#ff8888',
          fontSize: '0.82rem', cursor: 'pointer', backdropFilter: 'blur(12px)',
          textAlign: 'center', fontWeight: 600,
        }}>{toastMsg.msg}</div>
      )}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 rounded-2xl blur-xl"></div>
        
        <div className="relative bg-slate-900 border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">Meine Share Links</h2>
              <p className="text-slate-400 text-sm">{shareLinks.length} Links insgesamt</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Share Links Liste */}
          {shareLinks.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </div>
              <p className="text-slate-400">Noch keine Share Links erstellt</p>
            </div>
          ) : (
            <div className="space-y-3">
              {shareLinks.map((link) => (
                <div
                  key={link.id}
                  className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    {/* File Info */}
                    <div className="flex-1 min-w-0" style={{minWidth: '0', flexBasis: '200px'}}>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-white font-semibold truncate">{link.filename}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(link) === 'green' ? 'bg-green-500/20 text-green-400' : getStatusColor(link) === 'orange' ? 'bg-orange-500/20 text-orange-400' : 'bg-red-500/20 text-red-400'}`}>
                          {getStatusText(link)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-1.5 text-xs text-slate-400 mb-3">
                        <div>
                          <span className="opacity-70">Erstellt:</span> {formatDate(link.created_at)}
                        </div>
                        <div>
                          <span className="opacity-70">Läuft ab:</span> {formatDate(link.expires_at)}
                        </div>
                        <div>
                          <span className="opacity-70">Downloads:</span> {link.download_count}/{link.max_downloads || '∞'}
                        </div>
                        <div>
                          <span className="opacity-70">Zuletzt:</span> {link.last_downloaded_at ? formatDate(link.last_downloaded_at) : 'Nie'}
                        </div>
                      </div>

                      {/* Link */}
                      <div className="flex items-center gap-2 bg-black/30 rounded-lg p-2">
                        <input
                          type="text"
                          value={link.share_url}
                          readOnly
                          className="flex-1 bg-transparent text-cyan-400 text-sm outline-none truncate"
                        />
                        <button
                          onClick={() => copyToClipboard(link.share_url)}
                          className="p-1.5 hover:bg-white/10 rounded transition-colors"
                          title="Kopieren"
                        >
                          <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => generateQRCode(link.share_url)}
                        className="p-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg transition-all"
                        title="QR-Code anzeigen"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setDeleteModal(link)}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-all"
                        title="Löschen"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      {qrCodeModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <BodyScrollLock />
          <div className="relative max-w-md w-full">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 rounded-2xl blur-xl"></div>
            <div className="relative bg-slate-900 border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">QR-Code</h3>
                <button
                  onClick={() => setQrCodeModal(null)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="bg-white rounded-xl p-6 mb-4">
                <img src={qrCodeModal.qrDataUrl} alt="QR Code" className="w-full" />
              </div>

              <p className="text-xs text-slate-400 mb-4 break-all">{qrCodeModal.url}</p>

              <div className="flex gap-3">
                <button
                  onClick={downloadQRCode}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all"
                >
                  Als Bild speichern
                </button>
                <button
                  onClick={() => setQrCodeModal(null)}
                  className="px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all"
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <BodyScrollLock />
          <div className="relative max-w-md w-full">
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-orange-600/20 rounded-2xl blur-xl"></div>
            <div className="relative bg-slate-900 border border-white/10 rounded-2xl p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Share-Link löschen?</h3>
                <p className="text-slate-400 text-sm mb-1">Datei: <span className="text-white font-semibold">{deleteModal.filename}</span></p>
                <p className="text-red-400 text-sm">Diese Aktion kann nicht rückgängig gemacht werden!</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleDelete(deleteModal.id)}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-red-500/50 transition-all"
                >
                  Ja, löschen
                </button>
                <button
                  onClick={() => setDeleteModal(null)}
                  className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShareLinksManager;
